import { useEffect, type MutableRefObject, type Dispatch, type SetStateAction } from 'react';
import type { GuestReservationFormFields, GuestReservationFormProps } from './guestReservationFormTypes';

type PaymentMode = 'guest' | 'member';

export function useGuestReservationOpenBootstrap(params: {
  isOpen: boolean;
  itemId: string;
  itemName: string;
  shoppingCartData: GuestReservationFormProps['shoppingCartData'];
  currentUser: GuestReservationFormProps['currentUser'];
  itemType: GuestReservationFormProps['itemType'];
  initialPaymentMode: GuestReservationFormProps['initialPaymentMode'];
  t: (key: string) => string;
  isUpdatingFromExternalRef: MutableRefObject<boolean>;
  setFormData: Dispatch<SetStateAction<GuestReservationFormFields>>;
  setPaymentMode: Dispatch<SetStateAction<PaymentMode>>;
  setIsLoggedIn: Dispatch<SetStateAction<boolean>>;
  setLoggedInUserId: Dispatch<SetStateAction<string | null>>;
  setLoginData: Dispatch<SetStateAction<{ email: string; password: string }>>;
  setLoginError: Dispatch<SetStateAction<string>>;
  setRecommendedProducts: Dispatch<SetStateAction<unknown[]>>;
}): void {
  const {
    isOpen,
    itemId,
    shoppingCartData,
    currentUser,
    itemType,
    initialPaymentMode,
    t,
    isUpdatingFromExternalRef,
    setFormData,
    setPaymentMode,
    setIsLoggedIn,
    setLoggedInUserId,
    setLoginData,
    setLoginError,
    setRecommendedProducts,
  } = params;

  useEffect(() => {
    if (isOpen) {
      const totalAdults =
        shoppingCartData?.groups?.reduce((sum, group) => sum + (group.adults || 0), 0) || 1;
      const totalChildren =
        shoppingCartData?.groups?.reduce((sum, group) => sum + (group.kids || 0), 0) || 0;

      let checkInDate = '';
      let checkOutDate = '';
      if (shoppingCartData?.startDate) {
        const start = shoppingCartData.startDate;
        checkInDate = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
      }
      if (shoppingCartData?.endDate) {
        const end = shoppingCartData.endDate;
        checkOutDate = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
      }

      let duration = 1;
      if (checkInDate && checkOutDate) {
        const [checkInYear, checkInMonth, checkInDay] = checkInDate.split('-').map(Number);
        const [checkOutYear, checkOutMonth, checkOutDay] = checkOutDate.split('-').map(Number);
        const checkIn = new Date(checkInYear, checkInMonth - 1, checkInDay);
        const checkOut = new Date(checkOutYear, checkOutMonth - 1, checkOutDay);
        const diffTime = checkOut.getTime() - checkIn.getTime();
        duration = Math.max(1, Math.round(diffTime / (1000 * 60 * 60 * 24)));
      }

      const autoFillFirstName = currentUser?.firstName || '';
      const autoFillLastName = currentUser?.lastName || '';
      const autoFillEmail = currentUser?.email || '';
      const autoFillPhone = currentUser?.phone || '';

      isUpdatingFromExternalRef.current = true;

      setFormData((prev) => {
        const hasPersonalInfo = prev.firstName || prev.lastName || prev.email || prev.phone;
        return {
          firstName: hasPersonalInfo ? prev.firstName : autoFillFirstName,
          lastName: hasPersonalInfo ? prev.lastName : autoFillLastName,
          email: hasPersonalInfo ? prev.email : autoFillEmail,
          phone: hasPersonalInfo ? prev.phone : autoFillPhone,
          // Preserve address fields across re-opens
          addressLine1: prev.addressLine1 ?? '',
          postalCode: prev.postalCode ?? '',
          province: prev.province ?? 'QC',
          country: prev.country ?? 'CA',
          checkIn: checkInDate,
          checkOut: checkOutDate,
          duration,
          adults: prev.adults > 0 ? prev.adults : totalAdults,
          children: prev.children >= 0 ? prev.children : totalChildren,
          specialRequests: prev.specialRequests || '',
          hearAboutUs: prev.hearAboutUs || '',
          paymentMethod: prev.paymentMethod && prev.paymentMethod !== 'credit' && prev.paymentMethod !== 'cash' ? prev.paymentMethod : 'full',
          couponCode: prev.couponCode || '',
        };
      });

      if (initialPaymentMode) {
        setPaymentMode(initialPaymentMode);
        setIsLoggedIn(initialPaymentMode === 'member' && !!currentUser);
        setLoggedInUserId(initialPaymentMode === 'member' && currentUser ? currentUser.id : null);
      } else {
        if (currentUser) {
          setPaymentMode('member');
          setIsLoggedIn(true);
          setLoggedInUserId(currentUser.id);
        } else {
          setPaymentMode('guest');
          setIsLoggedIn(false);
          setLoggedInUserId(null);
        }
      }
      setLoginData({ email: '', password: '' });
      setLoginError('');
      setRecommendedProducts([]);
    }
  }, [
    itemId,
    params.itemName,
    isOpen,
    shoppingCartData,
    currentUser,
    itemType,
    initialPaymentMode,
    t,
  ]);
}
